import React from "react";
import useAdminApi from "@/hooks/useAdminApi";
import FlagBadge from "@/modules/racket_lab_admin/FlagBadge";
import { useRacketLabAdminT } from "../../i18n/racketLabAdminI18n";

/**
 * DispatchStatusBanner — Sub-sprint C.
 *
 * Polls `GET /admin/notifications/_ops/dispatch-status` once at mount.
 * Renders a strip showing provider + the three HARD flags
 * (mock_enabled, dispatch_enabled, q28_enabled) + the current outbox
 * size. If `dispatch_enabled=true` OR `q28_enabled=true` the strip
 * turns red — defence-in-depth visual cue that the mock-only contract
 * has been broken at the env level.
 *
 * QUICK 1.B.bis i18n migration: visible labels (`provider`, `mock`,
 * `dispatch`, `q28`, `outbox`, `ttl Nd`, `err N` and the headline
 * strings) are resolved via `racketLab.dispatch.*` / `racketLab.banners.*`.
 * The polled API payload is unchanged. `data-testid` unchanged.
 */
export const DispatchStatusBanner = () => {
  const t = useRacketLabAdminT();
  const { data, loading, error } = useAdminApi(
    "/admin/notifications/_ops/dispatch-status",
  );
  const breached =
    data?.dispatch_enabled === true || data?.q28_enabled === true;
  const cls = breached
    ? "border-rose-300 bg-rose-50 text-rose-900"
    : "border-emerald-300 bg-emerald-50 text-emerald-900";

  return (
    <div
      data-testid="rl-dispatch-status-banner"
      className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}
    >
      <span className="font-semibold">
        {breached
          ? t("banners.dispatch_real_enabled_stop")
          : t("banners.dispatch_mock_only")}
      </span>
      {loading && <span className="text-slate-500">…</span>}
      {error && <span className="text-rose-700">{t("dispatch.error_prefix", { status: error.status })}</span>}
      {!loading && !error && data && (
        <>
          <FlagBadge tone="info" labelKey="dispatch.flag_provider" value={data.provider} dataTestId="rl-banner-flag-provider" />
          <FlagBadge tone={data.mock_enabled ? "mock" : "hard"} labelKey="dispatch.flag_mock" value={data.mock_enabled} dataTestId="rl-banner-flag-mock" />
          <FlagBadge tone={data.dispatch_enabled ? "hard" : "safe"} labelKey="dispatch.flag_dispatch" value={data.dispatch_enabled} dataTestId="rl-banner-flag-dispatch" />
          <FlagBadge tone={data.q28_enabled ? "hard" : "safe"} labelKey="dispatch.flag_q28" value={data.q28_enabled} dataTestId="rl-banner-flag-q28" />
          <span className="ml-auto text-[10px] text-slate-600">
            {t("dispatch.outbox_label")}: <span data-testid="rl-banner-outbox-count" className="font-mono">{data.outbox_count}</span> · {t("dispatch.ttl_days_suffix", { days: data.ttl_days })}
          </span>
        </>
      )}
    </div>
  );
};

export default DispatchStatusBanner;
